import {
    Drawer,
    type DrawerProps,
    Flex,
    Header,
    pxArrayToRem,
    pxToRem,
    useAppDisplayModeContext,
    useScrollLock,
    useStyles,
    useTheme,
} from '@archipro-design/aria';
import type { PopupProps } from '@fluentui/react-northstar';
import type { ReactNode, Ref, SyntheticEvent } from 'react';
import { useRef } from 'react';
import * as S from './BottomDrawer.style';
import { useFacebookAutofill } from '~/modules/detail/hooks/useFacebookAutofill';
import { useLogger } from '@archipro-website/logger/client';

interface BottomDrawerProps {
    open: boolean;
    toggleModal: (value: boolean) => void;
    title?: ReactNode;
    children?: ReactNode;
    contentRef?: Ref<HTMLElement> | undefined;
    contentHeight?: number | string;
    className?: string;
}

const BottomDrawer = ({
    children,
    open,
    toggleModal,
    title,
    contentRef,
    contentHeight,
    className,
}: BottomDrawerProps) => {
    const theme = useTheme();
    const scrollRef = useRef<HTMLDivElement>(null);
    const appDisplayMode = useAppDisplayModeContext();
    const { css } = useStyles();

    // aria's useScrollLock predates React 19's nullable RefObject types; the
    // ref is only read after mount, so bridge the pre-React-19 signature here.
    useScrollLock(scrollRef as React.RefObject<HTMLDivElement>, open);

    const heightTypeGuard = (value?: number | string) => {
        if (typeof value === 'number') {
            return pxToRem(value);
        } else if (typeof value === 'string') {
            return value;
        }
    };

    const drawerGestureBlocking = (e: React.TouchEvent<HTMLDivElement>) => {
        const scrollContainer = scrollRef?.current;
        if (scrollContainer) {
            const { scrollHeight, offsetHeight, scrollTop } = scrollContainer;
            const maxRange = scrollHeight - offsetHeight;
            const isScrolling =
                maxRange > 0 && 0 < scrollTop && scrollTop < maxRange;

            if (isScrolling) {
                e.stopPropagation();
            }
        }
    };

    const { mustKeepOpen, isOpenInFacebookBrowser } = useFacebookAutofill({
        open,
    });
    const logger = useLogger();

    const drawerContent = (
        <Flex column onTouchMove={drawerGestureBlocking}>
            {title && (
                <Header
                    as="h3"
                    variables={{
                        ...theme.siteVariables.textStyles.Heading.Medium65,
                        fontSize: pxToRem(16),
                    }}
                    className={css(S.BottomDrawerTitle)}
                >
                    {title}
                </Header>
            )}
            <div
                ref={scrollRef}
                className={css(S.BottomDrawerContentContainer)}
            >
                <div>{children}</div>
            </div>
        </Flex>
    );

    return (
        <Drawer
            {...({
                contentRef,
                open: open || mustKeepOpen,
                onOpenChange: (
                    _: SyntheticEvent<HTMLElement, Event>,
                    data: PopupProps | undefined,
                ) => {
                    if (isOpenInFacebookBrowser) {
                        // wired behaver, once remove the logger.info, the FB auto-fill will close, keep it for now
                        logger.info(
                            `FB in-app Browser BottomDrawer onOpenChange mustKeepOpen: ${mustKeepOpen}`,
                            { customData: { data } },
                        );
                    }
                    if (!mustKeepOpen) {
                        toggleModal(!!data?.open);
                    }
                },
                anchor: 'bottom' as const,
                fluid: true,
                className,
                variables: {
                    contentBorderRadius: pxArrayToRem([12, 12, 0, 0]),
                    contentHeight: contentHeight
                        ? heightTypeGuard(contentHeight)
                        : undefined,
                    backdropColor: `rgba(0, 0, 0, 0.25)`,
                },
                swipeableEdge: {
                    onEdgeSwipe: (
                        e: SyntheticEvent,
                        dir: 'up' | 'down' | 'left' | 'right',
                    ) => {
                        return dir === 'down' && toggleModal(false);
                    },
                },
                appDisplayMode,
                content: drawerContent,
            } as DrawerProps)}
        />
    );
};

export default BottomDrawer;
